import React, { useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from '../../../../shared/lib/supabaseClient';
import { useForm, SubmitHandler, type Resolver, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import NonTickerConfirmationDialog from './NonTickerConfirmationDialog';
import TickerAutocomplete from './TickerAutocomplete';
import { BASE_URL } from '../../../../shared/lib/api';
import { useFormDraftPersistence, getFormDraftStorageKey, writeFormDraft } from '../../hooks/useFormDraftPersistence';

// Define expanded transaction types
const transactionTypes = [
    "Buy", "Sell", "Short Sell", "Buy to Cover", "Dividend", "Dividend in Lieu",
    "Call", "Put", "Call Sell", "Put Sell",
    "Deposit", "Withdrawal", "Interest", "Adjustment", "UNKNOWN"
] as const;
const selectableTransactionTypes = transactionTypes.filter(type => type !== 'UNKNOWN');

// NOTE: We derive the form type from the schema below to keep TS in sync with validation

// Zod Schema updated with new names and fields (let Zod infer types)
const manualTransactionSchema = z.object({
  id: z.string().optional(),
  type: z.enum(transactionTypes, { required_error: 'Transaction type is required' }),
  date: z.string()
    .min(1, 'Date & Time is required')
    .refine(val => !isNaN(Date.parse(val)), { message: "Invalid Date & Time format" })
    .refine(val => new Date(val) <= new Date(), { message: "Transaction date cannot be in the future" }),
  ticker: z.string()
    .max(10, 'Ticker symbol too long')
    .transform(val => val ? val.toUpperCase().trim() : val) 
    .optional(),
  quantity: z.number().positive('Quantity must be positive').optional(), // Renamed
  price_per_unit: z.number().positive('Price must be positive').optional(), // Renamed
  commission: z.preprocess((val) => {
    if (val === '' || val === null || val === undefined) return 0;
    if (typeof val === 'number' && isNaN(val)) return 0;
    return val;
  }, z.number()
    .min(0, 'Commission cannot be negative')
    .default(0)),
  // Options fields
  contracts: z.number().positive('Contracts must be positive').optional(),
  strike_price: z.number().positive('Strike price must be positive').optional(), // Added
  expiration_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/,'Date must be in YYYY-MM-DD format').optional(), // Added
  // Direct amount field with friendly blank handling
  total_amount: z.preprocess((val) => {
    if (val === '' || val === null || val === undefined) return undefined;
    if (typeof val === 'number' && isNaN(val)) return undefined;
    return val;
  }, z.number({ invalid_type_error: 'This field cannot be blank', required_error: 'This field cannot be blank' }).optional()),
  notes: z.string().max(500, 'Notes cannot exceed 500 characters').optional(), // Added
  isHidden: z.boolean().optional(), // Keep in schema for type compatibility, but not shown in UI
  is_manually_edited: z.boolean().optional(), // Keep optional to match form data
  data_quality_status: z.string().nullable().optional(),
  data_quality_reason: z.string().nullable().optional(),
  brokerage_transaction_id: z.string().nullable().optional(),
  brokerage_description: z.string().nullable().optional(),
  brokerage_type: z.string().nullable().optional(),
  brokerage_quantity: z.any().optional(),
})
.superRefine((data, ctx) => {
  if (data.type === 'UNKNOWN') {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Select a transaction type before saving', path: ['type'] });
  }
  // Require Ticker, Quantity, Price for Buy/Sell types
  if (['Buy', 'Sell', 'Short Sell', 'Buy to Cover'].includes(data.type)) {
    if (!data.ticker) ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Ticker is required for this type', path: ['ticker'] });
    if (data.quantity === undefined || data.quantity === null) ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Quantity is required for this type', path: ['quantity'] }); // Renamed path
    if (data.price_per_unit === undefined || data.price_per_unit === null) ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Price per Unit is required', path: ['price_per_unit'] }); // Renamed path
  }
  // Require Ticker for Dividend
  if (data.type === 'Dividend' && !data.ticker) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Ticker is required for Dividends', path: ['ticker'] });
  }
  // Require Amount for types where it's directly entered
  if (['Deposit', 'Withdrawal', 'Dividend', 'Dividend in Lieu', 'Interest', 'Adjustment'].includes(data.type)) {
      if (data.total_amount === undefined || data.total_amount === null) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Amount is required for this type', path: ['total_amount'] });
      } else if (data.type !== 'Adjustment' && data.total_amount < 0) {
          // For non-Adjustment types, amount must be non-negative
          ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Value must be greater than or equal to 0', path: ['total_amount'] });
      }
  }
  // Require Options fields for option types
  if (['Call', 'Put', 'Call Sell', 'Put Sell'].includes(data.type)) {
    if (!data.ticker) ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Underlying Ticker is required', path: ['ticker'] });
    if (data.contracts === undefined || data.contracts === null) ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Number of Contracts is required', path: ['contracts'] });
    if (data.strike_price === undefined || data.strike_price === null) ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Strike Price is required', path: ['strike_price'] }); // Added
    if (!data.expiration_date) ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Expiration Date is required', path: ['expiration_date'] }); // Added
    if (data.price_per_unit === undefined || data.price_per_unit === null) ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Premium per Contract is required', path: ['price_per_unit'] }); // Premium is price_per_unit
  }
});

// Export the form data type inferred from the schema (for external consumers)
export type ManualTransactionFormData = z.infer<typeof manualTransactionSchema>;
// Local alias to avoid symbol collisions with any imported/legacy types
type FormValues = z.infer<typeof manualTransactionSchema>;

// Helper to format a Date object into YYYY-MM-DDTHH:mm local time
const formatLocalDateForInput = (date: Date): string => {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0'); // Months are 0-indexed
    const day = date.getDate().toString().padStart(2, '0');
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
};

// Helper to get current date/time in required format for datetime-local
const getCurrentDateTimeLocal = () => {
    return formatLocalDateForInput(new Date()); // Use the new helper
};

const EQUITY_TRANSACTION_TYPES = ['Buy', 'Sell', 'Short Sell', 'Buy to Cover'] as const;

type LatestSplitInfo = { split_date: string; split_description: string };

export const isNeedsReviewTransaction = (transaction?: Pick<ManualTransactionFormData, 'data_quality_status'> | null) => {
  const status = String(transaction?.data_quality_status || 'verified').trim().toLowerCase();
  return status !== 'verified';
};

export const getDataQualityReviewMessage = (reason?: string | null): string => {
  switch (String(reason || '').trim()) {
    case 'pre_window_realized_sale':
      return 'This entry transaction was synthetically added to mirror an exit transaction from your recent import. The dates/values may not be fully accurate, please update them accordingly.';
    case 'pre_window_option_open':
      return 'This option opening transaction was synthetically added to match a closing option transaction from your recent import. The date/value may not be fully accurate, please update it accordingly.';
    case 'pre_window_short_open':
      return 'This short opening transaction was synthetically added to match a covering transaction from your recent import. The date/value may not be fully accurate, please update it accordingly.';
    case 'pre_window_position':
      return 'This opening transaction was synthetically added based on your current holdings because earlier purchase history was not available. Please confirm/edit the date and values. Note: There may be multiple transactions missed, this addition simply estimates the values to balance the share count accordingly.';
    case 'pre_window_residual_position':
      return 'This opening transaction was synthetically added for shares not explained by the imported activity window. Please confirm/edit the date and values. Note: There may be multiple transactions missed, this addition simply estimates the values to balance the share count accordingly.';
    case 'basis_mismatch':
      return 'This imported holding did not fully reconcile to the broker-reported cost basis. Please review the transaction details.';
    case 'unknown_brokerage_activity':
      return 'This imported transaction came from a brokerage activity type Vester does not fully recognize yet. Please review the transaction details.';
    default:
      return 'This imported transaction needs review because some details were inferred from brokerage data.';
  }
};

// Define component props
interface TransactionSubmitResult {
  success: boolean;
  needsTickerFetch?: boolean;
  newTicker?: string | null;
}

interface ManualTransactionFormProps {
  onSubmit: (data: FormValues) => Promise<TransactionSubmitResult>;
  onCancel: () => void;
  initialData?: FormValues; // For editing
  accountId: string; // Needed for onCancel navigation
  onSaved?: (opts: { isEdit: boolean; addAnother: boolean; successMessage?: string | null }) => void;
  onClosed?: (opts: { didSaveDuringSession: boolean }) => void;
}

const ManualTransactionForm: React.FC<ManualTransactionFormProps> = ({
  onSubmit,
  onCancel,
  initialData,
  accountId, // Receive accountId
  onSaved,
  onClosed,
}) => {
  // Default "Add another" checkbox to false
  const [addAnother, setAddAnother] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState<string | null>(null);
  const [showErrorMessage, setShowErrorMessage] = useState<string | null>(null);
  const [isProcessingSubmit, setIsProcessingSubmit] = useState(false);
  const [latestSplit, setLatestSplit] = useState<LatestSplitInfo | null>(null);
  const [showSplitWarning, setShowSplitWarning] = useState(false);
  const splitFetchGenerationRef = useRef(0);
  const didSaveDuringSessionRef = useRef(false);
  const isRestoringDraftRef = useRef(false);
  const prevTypeRef = useRef<FormValues['type'] | null>(null);
  const seededInitialDataIdRef = useRef<string | null>(null);
  const [showNonTickerDialog, setShowNonTickerDialog] = useState(false);
  const [pendingTicker, setPendingTicker] = useState<string | null>(null);
  const [pendingFormData, setPendingFormData] = useState<FormValues | null>(null);

  // Pin resolver identity concisely without generic gymnastics
  const resolver = zodResolver(manualTransactionSchema) as unknown as Resolver<FormValues>;

  const {
    register,
    handleSubmit,
    watch,
    control,
    formState: { errors, isSubmitting },
    reset,
    getValues,
    setValue,
  } = useForm<FormValues>({
    resolver,
    defaultValues: initialData ? 
      { // Map initialData field names
          ...initialData, 
          isHidden: initialData.isHidden ?? false,
          commission: initialData.commission ?? 0, 
          date: initialData.date?.slice(0, 16), 
          quantity: initialData.quantity, // Use new name
          price_per_unit: initialData.price_per_unit, // Use new name
          strike_price: initialData.strike_price, // Use new name
          expiration_date: initialData.expiration_date, // Use new name
          total_amount: (['Withdrawal', 'Dividend in Lieu'].includes(initialData.type as any)
            ? (typeof initialData.total_amount === 'number' ? Math.abs(initialData.total_amount) : initialData.total_amount)
            : initialData.total_amount), // positive UI for Withdrawal/Dividend in Lieu
          notes: initialData.notes, // Use new name
      } 
      : {
          // Default values with new names
          type: 'Buy',
          date: getCurrentDateTimeLocal(),
          ticker: '',
          quantity: undefined,
          price_per_unit: undefined,
          commission: 0, 
          contracts: undefined,
          strike_price: undefined,
          expiration_date: undefined,
          total_amount: undefined,
          notes: '',
          isHidden: false,
    },
  });

  const selectedType = watch('type');
  const isReviewEdit = !!initialData && isNeedsReviewTransaction(initialData);
  const quantity = watch('quantity'); // Use new name
  const pricePerUnit = watch('price_per_unit'); // Use new name
  const commission = watch('commission');
  const totalAmount = watch('total_amount'); // Use new name
  const contracts = watch('contracts');
  const ticker = watch('ticker');
  const date = watch('date');
  const isUnknownTypeSelection = selectedType === 'UNKNOWN';
  // Add watches for new fields if needed for effects
  // const strikePrice = watch('strike_price');
  // const expirationDate = watch('expiration_date');

  // Persist form drafts for both add and edit flows. Edit drafts are scoped by transaction id.
  const { clearDraft, didRestoreRef } = useFormDraftPersistence(
    'manualTransaction',
    accountId,
    watch,
    reset,
    {
      enabled: true,
      entityId: initialData?.id ?? null,
      isRestoringRef: isRestoringDraftRef,
    }
  );

  // Filter restored draft to only include fields relevant to current transaction type
  // This prevents validation errors when switching types (e.g., Call -> Dividend)
  useEffect(() => {
    if (!selectedType) return;
    if (isRestoringDraftRef.current) {
      prevTypeRef.current = selectedType;
      return;
    }
    const prevType = prevTypeRef.current;
    if (!prevType) {
      prevTypeRef.current = selectedType;
      return;
    }
    if (prevType === selectedType) return;
    prevTypeRef.current = selectedType;
    
    const currentValues = getValues();
    // Commission is intentionally not supported for some cash transaction types.
    // If we don't clear it, it can remain in RHF state + session draft and silently affect totals.
    const typesWithoutCommission = new Set(['Deposit', 'Withdrawal', 'Interest', 'Adjustment']);
    const relevantFields = new Set(['id', 'type', 'date', 'notes']);
    if (!typesWithoutCommission.has(selectedType)) {
      relevantFields.add('commission');
    }
    const isOptionType = ['Call', 'Put', 'Call Sell', 'Put Sell'].includes(selectedType);
    const isEquityType = ['Buy', 'Sell', 'Short Sell', 'Buy to Cover'].includes(selectedType);
    const isCashType = ['Deposit', 'Withdrawal', 'Interest', 'Adjustment', 'Dividend', 'Dividend in Lieu'].includes(selectedType);
    
    if (isEquityType) {
      relevantFields.add('ticker').add('quantity').add('price_per_unit');
    } else if (isCashType) {
      relevantFields.add('total_amount');
      if (selectedType === 'Dividend' || selectedType === 'Dividend in Lieu') relevantFields.add('ticker');
    } else if (isOptionType) {
      relevantFields.add('ticker').add('contracts').add('strike_price').add('expiration_date').add('price_per_unit');
    }
    
    // Check if any irrelevant fields have values
    let needsCleanup = false;
    const fieldsToClear: Partial<FormValues> = {};
    
    // Always clear option fields when switching away from option types
    // This prevents validation errors from invalid/partial dates or other option-specific values
    if (!isOptionType) {
      const optionFields = ['contracts', 'strike_price', 'expiration_date'];
      optionFields.forEach(field => {
        const value = (currentValues as Record<string, unknown>)[field];
        // Clear if field exists at all (even if empty/invalid) - don't rely on hasValue check
        if (value !== undefined) {
          needsCleanup = true;
          fieldsToClear[field as keyof FormValues] = undefined;
        }
      });
    }
    
    Object.keys(currentValues).forEach(key => {
      const value = (currentValues as Record<string, unknown>)[key];
      const hasValue = value !== undefined && value !== '' && value !== null && value !== 0;
      
      // Skip option fields - already handled above
      if (['contracts', 'strike_price', 'expiration_date'].includes(key)) {
        return;
      }
      
      // Clear other irrelevant fields
      if (!relevantFields.has(key) && hasValue) {
        needsCleanup = true;
        fieldsToClear[key as keyof FormValues] = undefined;
      }
    });
    
    // Clear irrelevant fields to prevent validation errors
    if (needsCleanup) {
      Object.keys(fieldsToClear).forEach(key => {
        setValue(key as keyof FormValues, undefined, { shouldValidate: false, shouldDirty: false });
      });
    }
  }, [selectedType, getValues, setValue]);

  // Clear draft when modal is explicitly closed (via onCancel - Back/Done/X)
  // Note: We do NOT clear on unmount because that would break tab-switch persistence
  const handleCancelWithClear = useCallback(() => {
    clearDraft();
    onClosed?.({ didSaveDuringSession: didSaveDuringSessionRef.current });
    onCancel();
  }, [clearDraft, onCancel, onClosed]);


  // Effect to populate the form when initialData changes (for editing)
  useEffect(() => {
      if (!initialData || !(initialData as any).id) {
          seededInitialDataIdRef.current = null;
          return;
      }

      const initialDataId = (initialData as any).id as string;
      if (didRestoreRef.current || seededInitialDataIdRef.current === initialDataId) {
          seededInitialDataIdRef.current = initialDataId;
          return;
      }

      seededInitialDataIdRef.current = initialDataId;
      if (initialData && (initialData as any).id) { 
          const initialDate = (initialData as any).date ? new Date((initialData as any).date as any) : new Date();
          const formattedData = {
              ...(initialData as any),
              date: formatLocalDateForInput(initialDate),
              ticker: (initialData as any).ticker || undefined,
              quantity: (initialData as any).quantity || undefined,
              price_per_unit: (initialData as any).price_per_unit || undefined,
              contracts: (initialData as any).contracts || undefined,
              strike_price: (initialData as any).strike_price || undefined,
              expiration_date: (initialData as any).expiration_date || undefined,
              total_amount: ((initialData as any).type && ['Withdrawal','Dividend in Lieu'].includes((initialData as any).type))
                ? (typeof (initialData as any).total_amount === 'number' ? Math.abs((initialData as any).total_amount) : (initialData as any).total_amount)
                : (initialData as any).total_amount || undefined,
              notes: (initialData as any).notes || '',
              isHidden: (initialData as any).isHidden ?? false,
              commission: (initialData as any).commission ?? 0,
          } as FormValues;
          reset(formattedData);
      }
  }, [didRestoreRef, initialData, reset]);

  const evaluateSplitWarning = useCallback(
    (split: LatestSplitInfo | null) => {
      if (!split) {
        setShowSplitWarning(false);
        return;
      }
      if (date) {
        setShowSplitWarning(new Date(date) < new Date(split.split_date));
        return;
      }
      if (initialData) {
        setShowSplitWarning((initialData as { split_after_date?: boolean }).split_after_date === true);
        return;
      }
      setShowSplitWarning(false);
    },
    [date, initialData]
  );

  const fetchLatestSplitForTicker = useCallback(
    async (tickerValue: string) => {
      const normalized = tickerValue.trim().toUpperCase();
      if (!normalized || !EQUITY_TRANSACTION_TYPES.includes(selectedType as typeof EQUITY_TRANSACTION_TYPES[number])) {
        setLatestSplit(null);
        setShowSplitWarning(false);
        return;
      }

      const generation = ++splitFetchGenerationRef.current;

      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) return;

        const response = await fetch(`${BASE_URL}/market/splits/${normalized}/latest`, {
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch latest split');
        }

        const data = await response.json();
        if (generation !== splitFetchGenerationRef.current) return;

        if (data.latest_split) {
          setLatestSplit(data.latest_split);
          evaluateSplitWarning(data.latest_split);
        } else {
          setLatestSplit(null);
          setShowSplitWarning(false);
        }
      } catch (error) {
        if (generation !== splitFetchGenerationRef.current) return;
        console.error('Error fetching latest split:', error);
        setLatestSplit(null);
        setShowSplitWarning(false);
      }
    },
    [selectedType, evaluateSplitWarning]
  );

  const handleTickerFieldBlur = useCallback(
    (tickerValue: string) => {
      void fetchLatestSplitForTicker(tickerValue);
    },
    [fetchLatestSplitForTicker]
  );

  const handleDateFieldBlur = useCallback(() => {
    if (!ticker?.trim()) return;
    void fetchLatestSplitForTicker(ticker);
  }, [ticker, fetchLatestSplitForTicker]);

  // Edit mode: check split once when the form opens with an existing equity ticker.
  useEffect(() => {
    if (!initialData?.ticker) return;
    const txType = initialData.type ?? selectedType;
    if (!EQUITY_TRANSACTION_TYPES.includes(txType as typeof EQUITY_TRANSACTION_TYPES[number])) return;
    void fetchLatestSplitForTicker(initialData.ticker);
  }, [initialData?.id, initialData?.ticker, initialData?.type, fetchLatestSplitForTicker, selectedType]);

  // Clear split state when the transaction type is not an equity trade.
  useEffect(() => {
    if (!EQUITY_TRANSACTION_TYPES.includes(selectedType as typeof EQUITY_TRANSACTION_TYPES[number])) {
      setLatestSplit(null);
      setShowSplitWarning(false);
    }
  }, [selectedType]);

  // Calculate amount based on type (use new field names)
  const calculatedAmount = React.useMemo(() => {
    const amt = (typeof totalAmount === 'number' && !isNaN(totalAmount)) ? totalAmount : 0;
    const commRaw = (typeof commission === 'number' && !isNaN(commission)) ? commission : 0;
    const comm = Math.max(0, commRaw); // commission is never negative

    if (['Dividend', 'Deposit', 'Interest', 'Adjustment', 'Withdrawal', 'Dividend in Lieu'].includes(selectedType)) {
        if (['Dividend', 'Deposit', 'Interest', 'Adjustment'].includes(selectedType)) {
            return amt - comm; // positive cash: net of commission
        }
        return -(amt + comm); // Withdrawal, Dividend in Lieu
    } else if (['Buy', 'Sell', 'Short Sell', 'Buy to Cover'].includes(selectedType)) {
        if (quantity && pricePerUnit) { // Use quantity, pricePerUnit
            const baseAmount = quantity * pricePerUnit;
            return ['Buy', 'Buy to Cover'].includes(selectedType) ? -(baseAmount + comm) : (baseAmount - comm);
        }
    } else if (['Call', 'Put', 'Call Sell', 'Put Sell'].includes(selectedType)) {
        if (contracts && pricePerUnit) { // Use pricePerUnit for premium
            const baseAmount = contracts * pricePerUnit * 100; 
            return ['Call', 'Put'].includes(selectedType) ? -(baseAmount + comm) : (baseAmount - comm);
        }
    }
    return 0;
  }, [selectedType, quantity, pricePerUnit, commission, totalAmount, contracts]);

  // Modified submit handler
  const handleFormSubmit: SubmitHandler<FormValues> = async (raw) => {
    if (isProcessingSubmit) return;
    setIsProcessingSubmit(true);
    setShowSuccessMessage(null);
    setShowErrorMessage(null);
  
    // normalize date to UTC ISO
    let submissionDateISO: string;
    try {
      submissionDateISO = new Date(raw.date).toISOString();
    } catch {
      setIsProcessingSubmit(false);
      return;
    }
  
    const equityTypes = ['Buy', 'Sell', 'Short Sell', 'Buy to Cover'] as const;
    const optionBuyTypes = ['Call', 'Put'] as const;
    const optionSellTypes = ['Call Sell', 'Put Sell'] as const;
    const cashTypes = ['Deposit', 'Withdrawal', 'Dividend', 'Dividend in Lieu', 'Interest', 'Adjustment'] as const;
  
    const shouldAttachCalculated =
      equityTypes.includes(raw.type as any) ||
      optionBuyTypes.includes(raw.type as any) ||
      optionSellTypes.includes(raw.type as any);
  
    const saveSigned = cashTypes.includes(raw.type as any);
  
    const values: FormValues = {
      ...raw,
      date: submissionDateISO,
      total_amount: saveSigned ? calculatedAmount : (shouldAttachCalculated ? calculatedAmount : raw.total_amount),
      is_manually_edited: true,
    };
  
    // Hand off to parent — this runs your handleManualTransactionSubmit
    try {
      const result = await onSubmit(values);
      
        if (result.success) {
        didSaveDuringSessionRef.current = true;
        
        // Determine success message
        const successMessage = result.needsTickerFetch
          ? "Transaction successfully added! Note that this is the first time we are adding this ticker information to the backend database. Please allow a little time before your portfolio is updated accordingly."
          : 'Transaction saved successfully!';
    
        if (addAnother && !raw.id) {
          // Show message in Add Transaction modal when "Add another" is checked
          setShowSuccessMessage(successMessage);
          setTimeout(() => setShowSuccessMessage(null), result.needsTickerFetch ? 10000 : 5000);
          
          // reset for "add another" - keep type, date, and ticker
          const keepType = values.type;
          const keepDateLocal = values.date.slice(0, 16); // back to datetime-local format
          const resetValues = {
            type: keepType,
            date: keepDateLocal,
            ticker: values.ticker ?? '',
            id: undefined,
            quantity: undefined,
            price_per_unit: undefined,
            commission: 0,
            contracts: undefined,
            strike_price: undefined,
            expiration_date: undefined,
            total_amount: undefined,
            notes: '',
            isHidden: false,
          };
          reset(resetValues);
          
          // Update draft with kept fields (type, date, ticker) for "add another"
          // Use centralized key builder to ensure consistency
          if (typeof window !== 'undefined') {
            const storageKey = getFormDraftStorageKey('manualTransaction', accountId, null);
            try {
              writeFormDraft(storageKey, resetValues as FormValues);
            } catch (e) {
              console.error('Failed to update draft for add another:', e);
            }
          }
          
          // Call onSaved without successMessage (staying in Add Transaction modal)
          onSaved?.({ isEdit: !!raw.id, addAnother });
        } else {
          // Clear draft on successful submit (not adding another)
          clearDraft();
          
          // Navigate back to Edit Account modal and pass success message
          onSaved?.({ isEdit: !!raw.id, addAnother, successMessage });
          onClosed?.({ didSaveDuringSession: didSaveDuringSessionRef.current });
          onCancel();
        }
      } else {
        // If onSubmit returns false but doesn't throw an error, show a generic error
        setShowErrorMessage('Failed to save transaction. Please try again.');
      }
    } catch (error: any) {
      // Check if this is a TICKER_NOT_FOUND_OPTIONAL error (for non-ticker support)
      if (error.code === 'TICKER_NOT_FOUND_OPTIONAL' || error.error_code === 'TICKER_NOT_FOUND_OPTIONAL' || error.message?.includes('TICKER_NOT_FOUND_OPTIONAL')) {
        // Store the ticker and form data for retry after creating non-ticker
        const tickerValue = values.ticker?.trim().toUpperCase() || null;
        setPendingTicker(tickerValue);
        setPendingFormData(values);
        setShowNonTickerDialog(true);
        setIsProcessingSubmit(false);
        return; // Don't show error message, dialog will handle it
      }
      
      // Check if this is a ticker not found error
      if (error.code === 'TICKER_NOT_FOUND') {
        // Use the friendly message for ticker not found errors
        setShowErrorMessage(error.message);
        
        // Focus the ticker input field to help the user correct it
        setTimeout(() => {
          const tickerInput = document.getElementById('ticker');
          if (tickerInput) {
            tickerInput.focus();
          }
        }, 100);
      } else {
        // For other errors, use the message if available, otherwise show a generic message
        setShowErrorMessage(error?.message || 'Failed to save transaction. Please try again.');
      }
    } finally {
      setIsProcessingSubmit(false);
    }
  };

  // Handle non-ticker confirmation: create non-ticker and retry submission
  const handleNonTickerConfirm = async () => {
    if (!pendingTicker || !pendingFormData) return;

    try {
      setIsProcessingSubmit(true);
      setShowNonTickerDialog(false);

      // Create non-ticker via API
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('No auth token');
      }

      const createRes = await fetch(`${BASE_URL}/market/symbols/manual`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ ticker: pendingTicker }),
      });

      if (!createRes.ok) {
        const createJson = await createRes.json().catch(() => ({}));
        throw new Error(createJson?.error || 'Failed to create custom ticker');
      }

      // Retry the transaction submission
      const result = await onSubmit(pendingFormData);
      
      if (result.success) {
        didSaveDuringSessionRef.current = true;
        
        // Determine success message (consistent with main submit handler)
        const successMessage = result.needsTickerFetch
          ? "Transaction successfully added! Note that this is the first time we are adding this ticker information to the backend database. Please allow a little time before your portfolio is updated accordingly."
          : 'Transaction saved successfully!';
        
        onSaved?.({ isEdit: !!pendingFormData.id, addAnother });
    
        if (addAnother && !pendingFormData.id) {
          // reset for "add another" - keep type, date, and ticker
          const keepType = pendingFormData.type;
          const keepDateLocal = pendingFormData.date.slice(0, 16); // back to datetime-local format
          const resetValues = {
            type: keepType,
            date: keepDateLocal,
            ticker: pendingFormData.ticker ?? '',
            id: undefined,
            quantity: undefined,
            price_per_unit: undefined,
            commission: 0,
            contracts: undefined,
            strike_price: undefined,
            expiration_date: undefined,
            total_amount: undefined,
            notes: '',
            isHidden: false,
          };
          reset(resetValues);
          
          // Update draft with kept fields (type, date, ticker) for "add another"
          // Use centralized key builder to ensure consistency
          if (typeof window !== 'undefined') {
            const storageKey = getFormDraftStorageKey('manualTransaction', accountId, null);
            try {
              writeFormDraft(storageKey, resetValues as FormValues);
            } catch (e) {
              console.error('Failed to update draft for add another:', e);
            }
          }
          
          setShowSuccessMessage(successMessage);
          setTimeout(() => setShowSuccessMessage(null), result.needsTickerFetch ? 10000 : 5000);
        } else {
          // Clear draft on successful submit (not adding another)
          clearDraft();
          
          onSaved?.({ isEdit: !!pendingFormData.id, addAnother, successMessage });
          onClosed?.({ didSaveDuringSession: didSaveDuringSessionRef.current });
          onCancel();
        }
      } else {
        setShowErrorMessage('Failed to save transaction after creating custom ticker. Please try again.');
      }
    } catch (error: any) {
      console.error('Error creating non-ticker or retrying submission:', error);
      setShowErrorMessage(error?.message || 'Failed to create custom ticker. Please try again.');
    } finally {
      setIsProcessingSubmit(false);
      setPendingTicker(null);
      setPendingFormData(null);
    }
  };

  // Handle non-ticker dialog cancel
  const handleNonTickerCancel = () => {
    setShowNonTickerDialog(false);
    setPendingTicker(null);
    setPendingFormData(null);
    setIsProcessingSubmit(false);
  };
  
  
  // --- Render Logic --- 
  const showTicker = ['Buy', 'Sell', 'Short Sell', 'Buy to Cover', 'Dividend', 'Dividend in Lieu', 'Call', 'Put', 'Call Sell', 'Put Sell'].includes(selectedType);
  const showQuantityPrice = ['Buy', 'Sell', 'Short Sell', 'Buy to Cover'].includes(selectedType);
  const showOptionsFields = ['Call', 'Put', 'Call Sell', 'Put Sell'].includes(selectedType);
  const showDirectAmount = ['Deposit', 'Withdrawal', 'Dividend', 'Dividend in Lieu', 'Interest', 'Adjustment'].includes(selectedType);
  const showCommission = !['Deposit', 'Withdrawal', 'Interest', 'Adjustment'].includes(selectedType);
  const showNotes = true; // Always show notes
  const isSubmitDisabled = isSubmitting || isProcessingSubmit || isUnknownTypeSelection;

  // --- DEBUG: Log validation errors (avoid circular structure issues) --- 
  if (Object.keys(errors).length > 0) {
      console.log("[Validation Errors]:", errors);
  }
  // --------------------------------- 

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4 p-1">
      {/* Success Message Display */} 
      {showSuccessMessage && (
         <div className="rounded-md bg-green-50 border border-green-200 p-4 mb-4">
            <p className="text-sm text-green-800">{showSuccessMessage}</p>
         </div>
      )}
      
      {/* Error Message Display */}
      {showErrorMessage && (
         <div className="rounded-md bg-red-50 p-4 mb-4">
            Error: {showErrorMessage}
         </div>
      )}

      {/* Split Warning Banner */}
      {initialData && isNeedsReviewTransaction(initialData) && (
        <div className="rounded-md bg-amber-50 border border-amber-200 p-4 mb-4">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <img
                src="/icon-info-lrg.png"
                alt="Needs review"
                className="h-5 w-5"
                style={{ filter: 'brightness(0) saturate(100%) invert(52%) sepia(88%) saturate(504%) hue-rotate(359deg) brightness(91%) contrast(88%)' }}
              />
            </div>
            <div className="ml-3 flex-1">
              <p className="text-sm font-medium text-amber-900">Needs Review</p>
              <p className="mt-1 text-sm text-amber-800">
                {getDataQualityReviewMessage(initialData.data_quality_reason)}
              </p>
            </div>
          </div>
        </div>
      )}

      {showSplitWarning && latestSplit && (
        <div className="rounded-md bg-amber-50 border border-amber-200 p-4 mb-4">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <img
                src="/icon-info-lrg.png"
                alt="Info"
                className="h-5 w-5 text-amber-600"
              />
            </div>
            <div className="ml-3 flex-1">
              <p className="text-sm text-amber-800">
                Note: A split occurred on {new Date(latestSplit.split_date).toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })} ({latestSplit.split_description}). Please enter the ORIGINAL 'Shares' and 'Price per Share' purchased at the time. We will translate these values accordingly.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Transaction Type Dropdown */} 
      <div className="flex-grow">
         <label htmlFor="type" className="block text-sm font-medium text-gray-700 mb-1">Type</label>
         <select
            id="type"
            {...register('type')}
            className={`block w-full px-3 py-2 border ${errors.type ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm ${isUnknownTypeSelection ? 'text-gray-400' : 'text-gray-900'}`}
         >
            {isUnknownTypeSelection && (
              <option value="UNKNOWN" disabled hidden>UNKNOWN</option>
            )}
            {selectableTransactionTypes.map(type => (
            <option key={type} value={type}>{type}</option>
            ))}
         </select>
         {errors.type && (
            <p className="mt-1 text-sm text-red-600">{errors.type.message}</p>
         )}
      </div>

      {/* Ticker and Date/Time Row */} 
      <div className="grid grid-cols-2 gap-4">
          {/* Ticker (Conditional) */} 
          {showTicker && (
              <div>
                <label htmlFor="ticker" className="block text-sm font-medium text-gray-700 mb-1">{showOptionsFields ? 'Underlying Ticker' : 'Ticker'}</label>
                <Controller
                  name="ticker"
                  control={control}
                  render={({ field }) => (
                    <TickerAutocomplete
                      id="ticker"
                      value={field.value ?? ''}
                      onChange={field.onChange}
                      onBlur={() => {
                        field.onBlur();
                        handleTickerFieldBlur(field.value ?? '');
                      }}
                      hasError={!!errors.ticker}
                      placeholder="e.g., AAPL"
                    />
                  )}
                />
                 {errors.ticker && (
                  <p className="mt-1 text-sm text-red-600">{errors.ticker.message}</p>
                )}
              </div>
          )} 
          {/* Spacer if Ticker is hidden */} 
          {!showTicker && <div />} 
          
          {/* Date & Time */} 
          <div className={`${!showTicker ? 'col-span-2' : ''}`}> 
            <label htmlFor="date" className="block text-sm font-medium text-gray-700 mb-1">Date & Time</label>
            <input 
              id="date" 
              type="datetime-local" 
              {...register('date', {
                onBlur: () => {
                  handleDateFieldBlur();
                },
              })}
              className={`block w-full px-3 py-2 border ${errors.date ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm`}
            />
            {errors.date && (
              <p className="mt-1 text-sm text-red-600">{errors.date.message}</p>
            )}
          </div>
      </div>

      {/* Shares / Price Grid (Conditional) */} 
      {showQuantityPrice && (
          <div className="grid grid-cols-2 gap-4">
            {/* Shares */} 
            <div>
              <label htmlFor="quantity" className="block text-sm font-medium text-gray-700 mb-1">Shares</label>
              <input 
                id="quantity" 
                type="number" 
                step="any" 
                {...register('quantity', { valueAsNumber: true })} 
                className={`block w-full px-3 py-2 border ${errors.quantity ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm`}
                placeholder="0"
              />
              {errors.quantity && (
                <p className="mt-1 text-sm text-red-600">{errors.quantity.message}</p>
              )}
            </div>
            {/* Price */} 
            <div>
               <label htmlFor="price_per_unit" className="block text-sm font-medium text-gray-700 mb-1">Price per Share</label>
                 <div className="relative mt-0 rounded-md shadow-sm">
                   <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3"><span className="text-gray-500 sm:text-sm">$</span></div>
                   <input 
                     id="price_per_unit" 
                     type="number" 
                     step="0.000001" 
                     inputMode="decimal"
                     {...register('price_per_unit', { valueAsNumber: true })} 
                     className={`block w-full rounded-md border py-2 pl-7 pr-3 sm:text-sm ${errors.price_per_unit ? 'border-red-300' : 'border-gray-300'} focus:outline-none focus:ring-green-500 focus:border-green-500`}
                     placeholder="0.00"
                   />
                 </div>
                 {errors.price_per_unit && (
                   <p className="mt-1 text-sm text-red-600">{errors.price_per_unit.message}</p>
                 )}
            </div>
          </div>
      )}
      
      {/* Options Fields Grid (Conditional) */} 
      {showOptionsFields && (
          <> 
            <div className="grid grid-cols-3 gap-4">
                {/* Contracts */} 
                <div>
                    <label htmlFor="contracts" className="block text-sm font-medium text-gray-700 mb-1">Contracts</label>
                    <input 
                        id="contracts" 
                        type="number" 
                        step="1" 
                        {...register('contracts', { valueAsNumber: true })} 
                        className={`block w-full px-3 py-2 border ${errors.contracts ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm`}
                        placeholder="0"
                    />
                    {errors.contracts && (
                        <p className="mt-1 text-sm text-red-600">{errors.contracts.message}</p>
                    )}
                </div>
                {/* Strike Price */} 
                <div>
                    <label htmlFor="strike_price" className="block text-sm font-medium text-gray-700 mb-1">Strike Price</label>
                     <div className="relative mt-0 rounded-md shadow-sm">
                         <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3"><span className="text-gray-500 sm:text-sm">$</span></div>
                         <input 
                            id="strike_price" 
                            type="number" 
                            step="0.01" 
                            {...register('strike_price', { valueAsNumber: true })} 
                            className={`block w-full rounded-md border py-2 pl-7 pr-3 sm:text-sm ${errors.strike_price ? 'border-red-300' : 'border-gray-300'} focus:outline-none focus:ring-green-500 focus:border-green-500`}
                            placeholder="0.00"
                         />
                     </div>
                    {errors.strike_price && (
                        <p className="mt-1 text-sm text-red-600">{errors.strike_price.message}</p>
                    )}
                </div>
                 {/* Expiration Date */} 
                <div>
                    <label htmlFor="expiration_date" className="block text-sm font-medium text-gray-700 mb-1">Expiration</label>
                    <input 
                        id="expiration_date" 
                        type="date" 
                        {...register('expiration_date')} 
                        className={`block w-full px-3 py-2 border ${errors.expiration_date ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm`}
                    />
                    {errors.expiration_date && (
                        <p className="mt-1 text-sm text-red-600">{errors.expiration_date.message}</p>
                    )}
                </div>
            </div>
             {/* Premium (using Price field) */} 
             <div>
                 <label htmlFor="price_per_unit" className="block text-sm font-medium text-gray-700 mb-1">Premium per Contract</label>
                 <div className="relative mt-0 rounded-md shadow-sm">
                   <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3"><span className="text-gray-500 sm:text-sm">$</span></div>
                   <input 
                     id="price_per_unit" 
                     type="number" 
                     step="0.000001" 
                     inputMode="decimal"
                     {...register('price_per_unit', { valueAsNumber: true })} 
                     className={`block w-full rounded-md border py-2 pl-7 pr-3 sm:text-sm ${errors.price_per_unit ? 'border-red-300' : 'border-gray-300'} focus:outline-none focus:ring-green-500 focus:border-green-500`}
                     placeholder="0.00"
                   />
                 </div>
                 {errors.price_per_unit && (
                   <p className="mt-1 text-sm text-red-600">{errors.price_per_unit.message}</p>
                 )}
             </div>
          </>
      )}

      {/* Direct Amount Input (Conditional) */} 
      {showDirectAmount && (
        (() => {
          // Types that should have Amount input and Calculated Amount on same row
          const showAmountSideBySide = ['Interest', 'Deposit', 'Withdrawal', 'Adjustment'].includes(selectedType);
          
          if (showAmountSideBySide) {
            // Side-by-side layout for Interest, Deposit, Withdrawal, Adjustment
            return (
              <div className="grid grid-cols-2 gap-4 items-end">
                {/* Amount Input */}
                <div>
                  <label htmlFor="total_amount" className="block text-sm font-medium text-gray-700 mb-1">Amount</label>
                  <div className="relative mt-0 rounded-md shadow-sm">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                      <span className="text-gray-500 sm:text-sm">$</span>
                    </div>
                    <input 
                      id="total_amount" 
                      type="number" 
                      step="0.000001" 
                      min={selectedType === 'Adjustment' ? undefined : 0}
                      {...register('total_amount', { valueAsNumber: true })} 
                      className={`block w-full rounded-md border py-2 pl-7 pr-3 sm:text-sm ${errors.total_amount ? 'border-red-300' : 'border-gray-300'} focus:outline-none focus:ring-green-500 focus:border-green-500`}
                      placeholder={"100.00"}
                    />
                  </div>
                  {errors.total_amount && (
                    <p className="mt-1 text-sm text-red-600">{errors.total_amount.message}</p>
                  )}
                </div>
                
                {/* Calculated Amount Display */}
                <div>
                  <span className="block text-sm font-medium text-gray-700 mb-1">Amount</span>
                  <p className={`mt-1 text-sm font-medium p-2 border border-gray-200 rounded-md bg-gray-50 text-right ${calculatedAmount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {(() => {
                      const val = (typeof calculatedAmount === 'number' && isFinite(calculatedAmount)) ? calculatedAmount : 0;
                      const sign = val === 0 ? '' : (val >= 0 ? '+' : '-');
                      return `${sign}$${Math.abs(val).toFixed(2)}`;
                    })()}
                  </p>
                </div>
              </div>
            );
          } else {
            // Original layout for Dividend, Dividend in Lieu
            return (
              <div>
                <label htmlFor="total_amount" className="block text-sm font-medium text-gray-700 mb-1">Amount</label>
                <div className="relative mt-0 rounded-md shadow-sm">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    <span className="text-gray-500 sm:text-sm">$</span>
                  </div>
                  <input 
                    id="total_amount" 
                    type="number" 
                    step="0.000001" 
                    min={selectedType === 'Adjustment' ? undefined : 0}
                    {...register('total_amount', { valueAsNumber: true })} 
                    className={`block w-full rounded-md border py-2 pl-7 pr-3 sm:text-sm ${errors.total_amount ? 'border-red-300' : 'border-gray-300'} focus:outline-none focus:ring-green-500 focus:border-green-500`}
                    placeholder={"100.00"}
                  />
                </div>
                {errors.total_amount && (
                  <p className="mt-1 text-sm text-red-600">{errors.total_amount.message}</p>
                )}
              </div>
            );
          }
        })()
      )}
      
      {/* Commission & Calculated Amount Row (Conditional Display of Amount) */} 
      {!['Interest', 'Deposit', 'Withdrawal', 'Adjustment'].includes(selectedType) && (
        <div className="grid grid-cols-2 gap-4 items-end">
          {/* Commission (Show unless it's a simple Deposit/Withdrawal?) */}
          {showCommission && (
            <div>
              <label htmlFor="commission" className="block text-sm font-medium text-gray-700 mb-1">Commission</label>
              <div className="relative mt-0 rounded-md shadow-sm">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3"><span className="text-gray-500 sm:text-sm">$</span></div>
                <input 
                  id="commission" 
                  type="number" 
                  step="0.01" 
                  min={0}
                  {...register('commission', { valueAsNumber: true })} 
                  className={`block w-full rounded-md border py-2 pl-7 pr-3 sm:text-sm ${errors.commission ? 'border-red-300' : 'border-gray-300'} focus:outline-none focus:ring-green-500 focus:border-green-500`}
                  placeholder="0.00"
                />
              </div>
              {errors.commission && (
                <p className="mt-1 text-sm text-red-600">{errors.commission.message}</p>
              )}
            </div>
          )} 
          {/* Spacer if commission hidden */}
          {!showCommission && <div />} 
          
          {/* Calculated/Entered Amount Display - only show for non-side-by-side types */}
          {!['Interest', 'Deposit', 'Withdrawal', 'Adjustment'].includes(selectedType) && (
            <div>
              <span className="block text-sm font-medium text-gray-700 mb-1">Amount</span>
              <p className={`mt-1 text-sm font-medium p-2 border border-gray-200 rounded-md bg-gray-50 text-right ${calculatedAmount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {(() => {
                  const val = (typeof calculatedAmount === 'number' && isFinite(calculatedAmount)) ? calculatedAmount : 0;
                  const sign = val === 0 ? '' : (val >= 0 ? '+' : '-');
                  return `${sign}$${Math.abs(val).toFixed(2)}`;
                })()}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Notes Field */}
      {showNotes && (
        <div>
          <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
          <input 
            id="notes" 
            type="text" 
            {...register('notes')} 
            className={`block w-full px-3 py-2 border ${errors.notes ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm`}
            placeholder="Optional notes"
            maxLength={500}
          />
          {errors.notes && (
            <p className="mt-1 text-sm text-red-600">{errors.notes.message}</p>
          )}
        </div>
      )}

      {/* Action Buttons & Add Another Checkbox */} 
      <div className="flex justify-between items-center pt-4">
         {/* Add Another Checkbox - Conditionally Rendered */} 
         {!initialData && ( // Only show when ADDING, not editing
           <div className="flex items-center">
             <input
                 id="addAnother"
                 name="addAnother"
                 type="checkbox"
                 checked={addAnother}
                 onChange={(e) => setAddAnother(e.target.checked)}
                 className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
             />
             <label htmlFor="addAnother" className="ml-2 block text-sm text-gray-900">
                 Add another transaction
             </label>
           </div>
         )}
         {/* Spacer if checkbox is hidden */}
         {initialData && <div />} 
         
         {/* Original Buttons */} 
         <div className="flex space-x-3">
           <button
             type="button"
             onClick={handleCancelWithClear} 
             className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
           >
             {/* Text depends on addAnother state ONLY IF not editing */} 
             {initialData ? 'Cancel' : (addAnother ? 'Cancel' : 'Done')} 
           </button>
           <button
             type="submit"
             disabled={isSubmitDisabled}
             className={`px-4 py-2 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 text-sm font-medium flex items-center justify-center ${
               isSubmitDisabled
                 ? 'bg-gray-300 cursor-not-allowed focus:ring-gray-300'
                 : 'bg-green-600 hover:bg-green-700 focus:ring-green-500'
             }`}
           >
             {(isSubmitting || isProcessingSubmit) && (
               <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                 <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                 <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
               </svg>
             )}
             {(isSubmitting || isProcessingSubmit)
               ? (isReviewEdit ? 'Confirming...' : 'Saving...')
               : (initialData ? (isReviewEdit ? 'Confirm transaction' : 'Save Changes') : 'Add Transaction')}
           </button>
         </div>
      </div>

      {/* Non-Ticker Confirmation Dialog */}
      <NonTickerConfirmationDialog
        isOpen={showNonTickerDialog}
        ticker={pendingTicker || undefined}
        onConfirm={handleNonTickerConfirm}
        onCancel={handleNonTickerCancel}
      />
    </form>
  );
};

export default ManualTransactionForm;
